/* eslint-disable max-len */
/**
 * AbandonSalesOrderModal Component
 * 
 * A modal component for abandoning a sales order.
 * Uses EVO Design System components with Tailwind CSS styling.
 * 
 * @component
 * @example
 * ```tsx
 * <AbandonSalesOrderModal
 *   isOpen={true}
 *   onClose={() => setIsOpen(false)}
 *   salesOrder={selectedSalesOrder}
 *   onSuccess={() => refetchGrid()}
 * />
 * ```
 */

import { useState, useCallback, useEffect, useMemo } from 'react';

import { EvoWrapper } from '@/components/evo/EvoWrapper';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/evo/ui/dialog';
import { Input } from '@/components/evo/ui/input';
import { Label } from '@/components/evo/ui/label';
import { Textarea } from '@/components/evo/ui/textarea';
import { Button } from '@/components/evo/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { SalesOrderStatusPills } from '@/pages/ContractDetails/RightPanel/Billing/EvoBillingCyclesModal/SalesOrderStatusPills';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import useAbandonSalesOrderByBillingCycle from '@/pages/ContractDetails/RightPanel/Billing/BillingCadence/hooks/useAbandonSalesOrderByBillingCycle';
import { SELECTIVE_BILLING_ABANDONED, SELECTIVE_BILLING_REINSTATE } from '@/config/reasonTypes.config';
import { SalesOrderSourcePascalCaseEnum, SalesOrderStatusEnum } from '@/graphql/contracts/_generated_/contracts';

interface SalesOrderData {
  salesOrderID?: string | null;
  source?: string | null;
  isAbandoned?: boolean | null;
  status?: {
    status?: string | null;
    formattedStatus?: string | null;
  } | null;
}

interface AbandonSalesOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  salesOrder: SalesOrderData | null;
  onSuccess?: () => void;
}

const REASON_TYPE_MAP = {
  abandon: { apiType: SELECTIVE_BILLING_ABANDONED, displayName: 'Selective Billing Invoice' },
  reinstate: { apiType: SELECTIVE_BILLING_REINSTATE, displayName: 'Selective Billing Reinvoice' },
};

export function AbandonSalesOrderModal({
  isOpen,
  onClose,
  salesOrder,
  onSuccess,
}: AbandonSalesOrderModalProps) {
  const { reasonCodes, isLoading: isLoadingReasonCodes, getReasonCodes } = useGetReasonCodes();
  const { abandonSalesOrderByBillingCycle, isLoadingAbandonSalesOrder } = useAbandonSalesOrderByBillingCycle();

  const [reasonCode, setReasonCode] = useState('');
  const [comment, setComment] = useState('');

  const isReinstate = salesOrder?.isAbandoned === true;
  const modalTitle = isReinstate ? 'Reinstate Sales Order' : 'Abandon Sales Order';
  const actionButtonText = isReinstate ? 'Reinstate' : 'Abandon';
  const placeholderText = isReinstate
    ? 'Enter reason for reinstating this sales order'
    : 'Enter reason for abandoning this sales order';

  const reasonTypeInfo = isReinstate ? REASON_TYPE_MAP.reinstate : REASON_TYPE_MAP.abandon;

  useEffect(() => {
    if (isOpen) {
      getReasonCodes(reasonTypeInfo.apiType);
      setReasonCode('');
      setComment('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reasonTypeInfo.apiType]);

  const reasonOptions = useMemo(() => {
    return reasonCodes.map((rc) => ({
      value: rc.code,
      label: rc.name,
      code: rc.code,
    }));
  }, [reasonCodes]);

  const handleReasonCodeChange = useCallback((value: string) => {
    setReasonCode(value);
  }, []);

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  }, []);

  const handleSave = useCallback(async () => {
    if (!salesOrder?.salesOrderID) {
      return;
    }

    await abandonSalesOrderByBillingCycle({
      soByBcId: salesOrder.salesOrderID,
      isAbandoned: !isReinstate,
      reasonCode,
      comment,
    });

    onClose();
    onSuccess?.();
  }, [salesOrder, reasonCode, comment, isReinstate, abandonSalesOrderByBillingCycle, onClose, onSuccess]);

  const isSaveDisabled = !reasonCode || !comment.trim() || isLoadingAbandonSalesOrder || isLoadingReasonCodes;

  const mapStatusToEnum = (status?: string | null): SalesOrderStatusEnum | null => {
    if (!status) return null;
    const statusMap: Record<string, SalesOrderStatusEnum> = {
      'Created': SalesOrderStatusEnum.CREATED,
      'Sending Paused': SalesOrderStatusEnum.SENDING_PAUSED,
      'Billing Paused': SalesOrderStatusEnum.BILLING_PAUSED,
      'Sending Scheduled': SalesOrderStatusEnum.SENDING_SCHEDULED,
      'Sending Failed': SalesOrderStatusEnum.SENDING_FAILED,
      'Sending Scheduling Failed': SalesOrderStatusEnum.SENDING_SCHEDULING_FAILED,
      'Sending Stopped': SalesOrderStatusEnum.SENDING_STOPPED,
      'Sent': SalesOrderStatusEnum.SENT,
    };
    return statusMap[status] || null;
  };

  const mapSourceToEnum = (source?: string | null): SalesOrderSourcePascalCaseEnum | null => {
    if (!source) return null;
    if (source === 'System' || source === 'Manual') {
      return source as SalesOrderSourcePascalCaseEnum;
    }
    return null;
  };

  return (
    <EvoWrapper>
      <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
          </DialogHeader>

          <SalesOrderStatusPills
            source={mapSourceToEnum(salesOrder?.source)}
            status={mapStatusToEnum(salesOrder?.status?.formattedStatus)}
          />

          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="reason-type">Reason Type</Label>
              <Input
                id="reason-type"
                value={reasonTypeInfo.displayName}
                disabled
                className="bg-muted cursor-not-allowed"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reason-name">
                Reason Name <span className="text-destructive">*</span>
              </Label>
              <Select value={reasonCode} onValueChange={handleReasonCodeChange}>
                <SelectTrigger id="reason-name">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {reasonOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="comment">
                Comment <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="comment"
                value={comment}
                onChange={handleCommentChange}
                placeholder={placeholderText}
                className="resize-none"
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaveDisabled}>
              {isLoadingAbandonSalesOrder ? 'Saving...' : actionButtonText}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </EvoWrapper>
  );
}
